import { Button, Card, Col, Divider, Empty, Form, Row, Spin, Table, Tag, Typography } from "antd";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useForm } from "antd/lib/form/Form";
import React, { useEffect, useRef, useState } from "react";

import TipoConsultaDataSelect from "@/Components/Populados/Financeiro/Titulo/TipoConsultaData/TipoConsultaDataSelect";
import PessoaSelectComTipo from "@/Components/Populados/Pessoa/PessoaSelect";
import { Parametros as ParametrosDaVisaoMensal, ResultadoDaVisaoMensal } from "@/Models/Financeiro/Titulo/Relatorios/RelatorioDeVisaoMensalDosTitulos";
import { EnumTipoConsultaData } from "@/Models/Financeiro/Titulo/Titulo";

import { Balanco } from "@/Components/Balanco";
import ComponentePrint, { IComponentePrintRef } from "@/Components/Base/Baixar/ComponentePrint";
import { TypeCentroCusto } from "@/Models/Financeiro/CentroCusto";
import { EnumPessoaTipo } from "@/Models/Pessoa/Pessoa";
import { useGerarRelatorioDeVisaoMensal } from "@/Services/Financeiro/Titulo/Relatorios/RelatoriosService";
import Formatacoes from "@/Utils/Formatacao";
import Ordenar from "@/Utils/Ordenacao";
import dayjs, { Dayjs } from "dayjs";
import CentroCustoSelect from "../../../CentroConta/CentroCustoSelect";
import InputRangePicker from "@/Components/Base/Input/InputRangePicker";

type TrilhaDeCodigosContaveis = ( string | undefined )[];
const TabelaDaVisaoMensal: React.FC<{
    dataSource?: ResultadoDaVisaoMensal[],
    style?: React.CSSProperties,
    tableProps?: Omit<React.ComponentProps<typeof Table<ResultadoDaVisaoMensal>>, 'dataSource'>,
    codigosContaveis?: TrilhaDeCodigosContaveis
}> = (props) => {
    const codigosContaveis = props.codigosContaveis ?? []
    const obterDescricao = (x: TrilhaDeCodigosContaveis) => x.indexOf(undefined) == -1 ? x.join(".") : "N/A";

    if(props.dataSource == undefined) return undefined;

    return (
        <Table<ResultadoDaVisaoMensal>
            rowKey={(r) => r.planoDeContas.codigo!}
            dataSource={props.dataSource.sort((a, b) => Ordenar.String(a.planoDeContas.codigoContabil, b.planoDeContas.codigoContabil))}
            pagination={false}
            scroll={{ x: true }}
            showHeader={false}
            {...props.tableProps}
            expandable={{
                indentSize: 0,
                rowExpandable: () => true,
                defaultExpandAllRows: true,
                expandedRowClassName: (x) => "recursive-table-wrapper-no-padding",
                expandedRowRender: (record) => {
                    const obterNomeDoMes = (month: number) => {
                        const formatter = new Intl.DateTimeFormat("pt-BR", { month: "short" });
                        return formatter.format(new Date(new Date().getFullYear(), Math.min(month, 12) - 1));
                    };

                    return (
                        <React.Fragment>
                            <Card>
                                <table className="pretty-table" style={{ textAlign: "left" }}>
                                    <tbody>
                                        <tr>
                                            <th>#</th>
                                            {record.resultados.map((r) => (
                                                <th key={`${r.ano}-${r.mes}`}>{`${r.ano}/${obterNomeDoMes(r.mes)}`}</th>
                                            ))}
                                        </tr>

                                        <tr>
                                            <th> Titulos </th>
                                            {record.resultados.map((r) => (
                                                <td key={r.ano + "-" + r.mes}>
                                                    <Tag>{r.acumulado.numeroDeTitulos}</Tag>
                                                </td>
                                            ))}
                                        </tr>

                                        <tr>
                                            <th> Balanço </th>
                                            {record.resultados.map((r) => (
                                                <td key={r.ano + "-" + r.mes}>
                                                    <Balanco>{Formatacoes.formatarReal(r.acumulado.valorPago)}</Balanco>
                                                </td>
                                            ))}
                                        </tr>

                                        <tr>
                                            <th> Previsto </th>
                                            {record.resultados.map((r) => (
                                                <td key={r.ano + "-" + r.mes}>
                                                    <Balanco> {Formatacoes.formatarReal(r.acumulado.valorPrevisto)} </Balanco>{" "}
                                                </td>
                                            ))}
                                        </tr>

                                        <tr>
                                            <th> Pendente </th>
                                            {record.resultados.map((r) => (
                                                <td key={r.ano + "-" + r.mes}>
                                                    <Balanco>{Formatacoes.formatarReal(r.acumulado.valorPendente)}</Balanco>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </Card>

                            {record.filhos && (
                                <TabelaDaVisaoMensal
                                    dataSource={record.filhos}
                                    codigosContaveis={[...codigosContaveis, record.planoDeContas.codigoContabil]}
                                />
                            )}
                        </React.Fragment>
                    );
                },
            }}
            columns={[
                {
                    title: "Descrição",
                    key: "descricao",
                    render: (_, record) => <Typography.Text strong>
                        {obterDescricao([...codigosContaveis, record.planoDeContas.codigoContabil])} - {record.planoDeContas.descricao}
                    </Typography.Text>
                },
            ]}
        />
    );

}

type IFiltro = Omit<ParametrosDaVisaoMensal, "de" | "ate" | "codigoDoCentroDeCustos"> & {
    intervalo: [ Dayjs, Dayjs ],
    tipoDaPessoa: EnumPessoaTipo,
    centroDeCustos?: TypeCentroCusto
}

const Filtro: React.FC<{}> = () => {
    const form = useFormInstance<IFiltro>()

    if(form == undefined) throw new Error("O componente deve estar aninhado em um formulário");

    return (
        <React.Fragment>
            <Row gutter={[8, 8]}>
                <Col xs={24} md={10} lg={4}>
                    <Form.Item label="Tipo de intervalo" initialValue={EnumTipoConsultaData.DataVencimento} rules={[{ required : true, message: "Selecione o valor base do intervalo" }]} name={["tipoDeConsulta"]}>
                        <TipoConsultaDataSelect />
                    </Form.Item>
                </Col>

                <Col xs={24} md={14} lg={5}>
                    <Form.Item<IFiltro>
                        label="Intervalo"
                        name={["intervalo"]}
                        rules={[
                            {
                                required: true,
                                message: "Selecione o intervalo da busca"
                            },
                        ]}
                        initialValue={[
                            dayjs().startOf('year'),
                            dayjs().endOf('year'),
                        ]}
                    >
                        <InputRangePicker
                            allowClear
                            picker="month"
                            disabledDate={(dia, v) => {
                                if (v.from) {
                                    if (dia < v.from) return true;
                                    if (dia > v.from.add(11, "months")) return true;
                                }
                                return false;
                            }}
                        />
                    </Form.Item>
                </Col>


                <Col xs={24} md={17} lg={10}>
                    <PessoaSelectComTipo<IFiltro>
                        names={{
                            tipo: ["tipoDaPessoa"],
                            "*": ["codigoDaPessoa"],
                        }}
                        rules={{
                            tipo: [
                                {required: true, message: "Selecione o tipo da pessoa"}
                            ],
                            "*": [
                                {required: true, message: "Selecione a pessoa"}
                            ],
                        }}
                    />
                </Col>

                <Col xs={24} md={7} lg={5}>
                    <Form.Item<IFiltro>
                        label="Centro de custos"
                        name={"centroDeCustos"}
                    >
                        <CentroCustoSelect style={{whiteSpace: "break-spaces"}}/>
                    </Form.Item>
                </Col>

            </Row>
        </React.Fragment>
    );
}

export const RelatorioDeVisaoMensalDosTitulos: React.FC<{}> = () => {

    const refPrint = useRef<IComponentePrintRef|null>(null);

    const [ formularioDeFiltro ] = useForm<IFiltro>()
    const [filtro, setFiltro] = useState<ParametrosDaVisaoMensal>()

    const relatorioQuery = useGerarRelatorioDeVisaoMensal({enabled: false, gcTime: 0}, filtro)

    const onFinish = (novoFiltro: IFiltro)  => {

        let de = novoFiltro.intervalo[0];
        let ate = novoFiltro.intervalo[1];

        de = de.startOf('month')
        ate = ate.endOf('month')
        
        setFiltro({
            de: de,
            ate: ate,
            codigoDaPessoa: novoFiltro.codigoDaPessoa,
            tipoDeConsulta: novoFiltro.tipoDeConsulta,
            codigoDoCentroDeCustos: novoFiltro.centroDeCustos?.codigo,
            situacao: novoFiltro.situacao
        })
    }

    useEffect(() => {
        if(filtro != undefined) relatorioQuery.refetch()
    }, [filtro])

    useEffect(() => {
    }, [relatorioQuery])

    return (
        <div>
            <ComponentePrint ref={refPrint}>
                <Form<IFiltro> form={formularioDeFiltro} layout="vertical" validateTrigger="onBlur" onFinish={onFinish}>
                    <Filtro />

                    <Row gutter={8}>
                        <Col>
                            <Button htmlType="submit" className="nao-imprimir" type="primary">
                                Obter relatório
                            </Button>
                        </Col>
                        <Col>
                            <Button className="nao-imprimir" type="dashed" onClick={() => refPrint.current?.imprimir()}>
                                Exportar
                            </Button>
                        </Col>
                    </Row>
                </Form>

                <Divider />

                <Spin spinning={relatorioQuery.isLoading}>
                    <Row gutter={[8, 8]}>
                        <Col xs={24}>
                            {relatorioQuery.data?.data == undefined || relatorioQuery.data.data.resultado.length == 0 ? (
                                relatorioQuery.isSuccess ? (
                                    <Empty description="Não há dados" style={{ width: "100%" }} />
                                ) : undefined
                            ) : (
                                ((d = relatorioQuery.data.data) => (
                                    <React.Fragment>
                                        <Row gutter={[8, 8]}>
                                            <Col xs={24}>
                                                <Card title={<> Acumulado do período </>}>
                                                    <table className="pretty-table" style={{ width: "-webkit-fill-available", textAlign: "center" }}>
                                                        <thead>
                                                            <th>Titulos</th>
                                                            <th>Balanço</th>
                                                            <th>Valor previsto</th>
                                                            <th>Valor pendente</th>
                                                        </thead>
                                                        <tbody>
                                                            <React.Fragment>
                                                                <td>{d.acumuladoDoPeriodo.numeroDeTitulos}</td>
                                                                <td>
                                                                    <Balanco>{Formatacoes.formatarReal(d.acumuladoDoPeriodo.valorPago)} </Balanco>{" "}
                                                                </td>
                                                                <td>
                                                                    <Balanco>{Formatacoes.formatarReal(d.acumuladoDoPeriodo.valorPrevisto)}</Balanco>
                                                                </td>
                                                                <td>
                                                                    <Balanco>{Formatacoes.formatarReal(d.acumuladoDoPeriodo.valorPendente)}</Balanco>
                                                                </td>
                                                            </React.Fragment>
                                                        </tbody>
                                                    </table>
                                                </Card>
                                            </Col>

                                            <Col xs={24}>
                                                <TabelaDaVisaoMensal dataSource={d.resultado} />
                                            </Col>
                                        </Row>
                                    </React.Fragment>
                                ))()
                            )}
                        </Col>
                    </Row>
                </Spin>
            </ComponentePrint>
        </div>
    );
}
